// src/hooks/useModulos.js
import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';

// Valores por defecto para todos los módulos (todos activos)
const getDefaultModulos = () => {
  return {
    // Comunidad
    comunidad: true,
    chat_comunitario: true,
    asistente_virtual_ia: true,
    // Amenidades
    amenidades: true,
    // Vigilancia
    vigilancia: true,
    validar_qr: true,
    generar_qr_invitado: true,
    registrar_paquete: true,
    visitas_frecuentes: true,
    qr_multi_acceso: true,
    qr_contador_escaneos: true,
    // Finanzas
    finanzas: true,
    botones_prueba_admin: true,
    boton_ajuste_saldos_admin: true,
    // Administración
    administracion: true,
    desactivacion_rfid_morosos: true,
    // Configuraciones
    configuraciones: true,
    modulo_migracion_regularizacion: true,
    modulo_gestion_saldos: true,
    modulo_importar_historico: true,
  };
};

/**
 * Hook custom para obtener los módulos habilitados de un fraccionamiento
 * Usa onSnapshot para escuchar cambios en tiempo real
 * @param {string} fraccionamientoId - ID del fraccionamiento
 * @returns {Object} { modulos: {...}, loading: boolean, error: string|null }
 */
export const useModulos = (fraccionamientoId) => {
  const [modulos, setModulos] = useState(getDefaultModulos());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!fraccionamientoId) {
      console.log('🟡 [useModulos] No hay fraccionamientoId, usando defaults');
      setModulos(getDefaultModulos());
      setLoading(false);
      return;
    }

    console.log('🔵 [useModulos] Configurando listener en tiempo real para fraccionamientoId:', fraccionamientoId);
    setLoading(true);
    setError(null);

    const docRef = doc(db, 'fraccionamientos', fraccionamientoId);
    let isMounted = true;
    
    // Usar onSnapshot para escuchar cambios en tiempo real
    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (!isMounted) return;
        try {
          if (docSnap.exists()) {
            const data = docSnap.data();
            const modulosHabilitados = data.modulosHabilitados || {};
            
            // Combinar defaults con valores existentes
            const defaults = getDefaultModulos();
            const modulosData = {
              ...defaults,
              ...modulosHabilitados,
            };
            
            // Asegurar que los valores false se mantengan
            Object.keys(modulosHabilitados).forEach(key => {
              if (modulosHabilitados[key] === false) {
                modulosData[key] = false;
              }
            });
            
            setModulos(modulosData);
            setLoading(false);
          } else {
            setModulos(getDefaultModulos());
            setLoading(false);
          }
        } catch (err) {
          console.error('❌ [useModulos] Error procesando snapshot:', err);
          setError(err.message);
          setLoading(false);
        }
      },
      (err) => {
        if (!isMounted) return;
        console.error('❌ [useModulos] Error en listener de Firestore:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    // Cleanup: desuscribirse cuando el componente se desmonte o cambie el fraccionamientoId
    return () => {
      isMounted = false;
      console.log('🔄 [useModulos] Limpiando listener para fraccionamientoId:', fraccionamientoId);
      unsubscribe();
    };
  }, [fraccionamientoId]);

  return { modulos, loading, error };
};

/**
 * Hook para verificar si un módulo específico está habilitado
 * @param {string} fraccionamientoId - ID del fraccionamiento
 * @param {string} modulo - Nombre del módulo
 * @returns {Object} { isEnabled: boolean, loading: boolean }
 */
export const useIsModuloHabilitado = (fraccionamientoId, modulo) => {
  const { modulos, loading } = useModulos(fraccionamientoId);
  const isEnabled = modulos[modulo] !== false;

  return { isEnabled, loading };
};
